/**
 * `GET /v1/docs/access` — session-gated endpoint returning the tenant
 * integration-docs document. Consumed by the tenant console and the desktop
 * client; both become renderers of this document instead of hardcoding copy.
 * Both already hold a tenant session before they can show the page this
 * replaces, so the gate costs neither of them a request — and it is what
 * lets a later batch put per-user facts in the body without changing the
 * endpoint's auth model, which would be a breaking change for two shipped
 * renderers.
 *
 * Owner: platform-api.
 *
 * WHAT IT IS FOR: the same job `CatalogSkuBilling` already does for the
 * price list (#1883). The tenant frontend writes ONE renderer against this
 * document and never learns a per-section special case — a new snippet, a
 * new callout, a new tab axis arrives as new DATA in the same syntax, with
 * zero frontend change. The syntax version moves only when the SHAPE
 * changes incompatibly; a reader seeing a version it does not know, or a
 * `type` it does not know, skips that block / falls back conservatively
 * rather than guessing.
 *
 * WHERE IT COMES FROM: compiled TypeScript constants in platform-api
 * (`src/docs/access/`) are the fallback, used only when
 * `docs_access_versions` has no row for the requested brand. Live content
 * is published to that append-only table by
 * `scripts/docs-access-publish.ts` — the only sanctioned writer — and
 * served from there. A copy change is a publish, not a deploy; adding a
 * member to a closed vocabulary is a router PR first. There is still no
 * per-tenant tailoring: one body per brand, so one ETag serves every
 * caller of that brand. Every environment- or catalog-derived value is a
 * placeholder the client substitutes at render time — baking a hostname
 * or a live SKU name into the body would make a docs fix a
 * three-environment frontend release, which is the failure mode this
 * endpoint exists to end.
 *
 * WHAT IT NEVER CARRIES: environment hostnames, live catalog model names,
 * tenant credentials, or any upstream/channel/routing fact. The tenant
 * black-box rule applies to this document exactly as to the catalog.
 *
 * WHY SEVEN BLOCK TYPES, NOT THE THREE THE ISSUE PROPOSED. The GitHub
 * issue listed `callout` / `snippet` / `param_table`. Those three do not
 * cover the live page: `Docs.tsx` also renders numbered steps, key/value
 * lists, checklists, fact cards, action links, and mapping cards, and
 * "first batch equals the live page section by section" is an acceptance
 * criterion. Seven is still a closed set — a renderer that meets an
 * unknown `type` skips that block. Expanding to "whatever JSX the page
 * happens to use" would make the renderer a second copy of the page,
 * which is the coupling this document is supposed to break. Mapping
 * cards and the page-header fact cards are encoded with `callout`
 * (`tone: 'fact'`) plus `key_values`; they do not get their own types.
 *
 * ADDITIVE OPTIONAL MEMBERS do not bump this version. The version moves
 * only when an EXISTING document's shape changes incompatibly — the same
 * rule `CatalogSkuBilling` documents.
 */

import type { BrandId } from './brands.ts';
import type { BilingualLabel } from './metering-presentation.ts';

export const DOCS_ACCESS_PRESENTATION_VERSION = 1;

/**
 * The five static placeholders. The client substitutes runtime values;
 * the document itself is brand-neutral and environment-neutral because
 * of these, not because the strings happen to look generic.
 *
 * `{{gateway_origin}}` is the tenant's gateway origin WITHOUT a `/v1`
 * suffix (the Anthropic base). `{{openai_base}}` is that origin plus
 * `/v1`. Splitting them is load-bearing: the most common integration
 * mistake on the live page is putting `/v1` on the Anthropic entry (or
 * leaving it off the OpenAI one), and a single "base URL" placeholder
 * would make that mistake inexpressible in the templates.
 *
 * `{{brand_name}}` is the product's display name ("NaDouTong" /
 * "Nxtpath"). The copy this document transcribes was written for one
 * console and names that brand in prose ("To route it through Nxtpath,
 * …"), which a document served to BOTH brands cannot carry. The server
 * knows the brand and could bake it, but baking would force a deep
 * string rewrite over otherwise-literal content modules, and the client
 * already knows its own brand for every other chrome string. Treating
 * the name as one more substitution keeps the content modules literal
 * data and keeps the two brands on one reviewed body.
 */
export const DOCS_ACCESS_STATIC_PLACEHOLDERS = [
  '{{gateway_origin}}',
  '{{openai_base}}',
  '{{console_origin}}',
  '{{api_key_placeholder}}',
  '{{brand_name}}',
] as const;

export type DocsStaticPlaceholder = (typeof DOCS_ACCESS_STATIC_PLACEHOLDERS)[number];

/**
 * Closed capability union for `{{model:x}}` / `{{models:x}}`. `unresolved`
 * is the verify-section curl: the SPA cannot know which Claude namespace
 * accepts plain-API traffic, so showing a real catalog name would be
 * worse than showing the literal `namespace/model` fallback.
 */
export const DOCS_MODEL_CAPABILITIES = [
  'anthropic',
  'openai',
  'image',
  'video',
  'seedance',
  'minimax',
  'unresolved',
] as const;

export type DocsModelCapability = (typeof DOCS_MODEL_CAPABILITIES)[number];

/**
 * Matches one `{{…}}` token inside a larger string. Used by both the
 * placeholder-guard test and any renderer that walks copy looking for
 * substitutions. Not global on the exported object so `lastIndex` cannot
 * leak across callers; `String.prototype.matchAll` still works because it
 * clones the regex.
 */
export const DOCS_PLACEHOLDER_TOKEN_RE = /\{\{[^{}]+\}\}/g;

/**
 * Full-token matchers for the two model forms. Capability is a capture so
 * the parser can reject an unknown capability rather than silently
 * treating `{{model:foo}}` as a static token it does not know.
 */
export const DOCS_MODEL_PLACEHOLDER_RE = /^\{\{model:([a-z0-9_]+)\}\}$/;
export const DOCS_MODELS_PLACEHOLDER_RE = /^\{\{models:([a-z0-9_]+)\}\}$/;

export type DocsPlaceholder =
  | { readonly form: 'static'; readonly token: DocsStaticPlaceholder }
  | { readonly form: 'model'; readonly capability: DocsModelCapability }
  | { readonly form: 'models'; readonly capability: DocsModelCapability };

export function isDocsModelCapability(raw: string): raw is DocsModelCapability {
  for (const candidate of DOCS_MODEL_CAPABILITIES) {
    if (candidate === raw) return true;
  }
  return false;
}

export function isDocsStaticPlaceholder(raw: string): raw is DocsStaticPlaceholder {
  for (const candidate of DOCS_ACCESS_STATIC_PLACEHOLDERS) {
    if (candidate === raw) return true;
  }
  return false;
}

/**
 * Parse one full `{{…}}` token. Returns null for anything outside the
 * closed set — including a well-shaped `{{model:foo}}` whose capability
 * is not in `DOCS_MODEL_CAPABILITIES` — so a renderer and the
 * placeholder-guard test agree on "unknown means skip / fail", not on
 * a guessed substitution.
 */
export function parseDocsPlaceholder(token: string): DocsPlaceholder | null {
  if (isDocsStaticPlaceholder(token)) {
    return { form: 'static', token };
  }
  const one = DOCS_MODEL_PLACEHOLDER_RE.exec(token);
  if (one !== null) {
    const capability = one[1];
    if (capability !== undefined && isDocsModelCapability(capability)) {
      return { form: 'model', capability };
    }
    return null;
  }
  const all = DOCS_MODELS_PLACEHOLDER_RE.exec(token);
  if (all !== null) {
    const capability = all[1];
    if (capability !== undefined && isDocsModelCapability(capability)) {
      return { form: 'models', capability };
    }
    return null;
  }
  return null;
}

/**
 * The selection rule the document carries, not just the token. Today this
 * rule lives in `Docs.tsx` (~lines 465–495) and the fallback literals in
 * `docsSnippets.ts`, so a new SKU family is a frontend release. Putting
 * the rule in the document is the whole point of the endpoint.
 *
 * Resolution (client's obligation):
 * - `vendor` — SKUs of that vendor slug, in catalog order.
 * - `vendor_name_contains` — SKUs of that vendor whose lowercased
 *   `sku_name` contains the needle.
 * - `name_contains` — the whole catalog, lowercased `sku_name` contains
 *   needle. Used for `xai/grok-imagine-video`, Seedance, and MiniMax,
 *   which are not under the `openai` vendor slice.
 * - `none` — never resolved from the catalog; always the fallback. The
 *   verify-section curl uses this so a Claude-Code-only namespace is
 *   never presented as a curl-able name.
 * - `{{model:x}}` takes the first match, or the fallback when there is
 *   none. `{{models:x}}` joins every match with ` / `, or the fallback
 *   when none.
 */
export type DocsModelMatch =
  | { readonly by: 'vendor'; readonly vendor: string }
  | { readonly by: 'vendor_name_contains'; readonly vendor: string; readonly needle: string }
  | { readonly by: 'name_contains'; readonly needle: string }
  | { readonly by: 'none' };

export interface DocsModelSelector {
  readonly capability: DocsModelCapability;
  readonly match: DocsModelMatch;
  /** Illustrative literal used when the catalog yields nothing. Canonical `namespace/model`. */
  readonly fallback: string;
}

/**
 * Tab axes. Shape deliberately mirrors `CatalogBillingAxis` /
 * `CatalogBillingTierRow.variant` so a renderer that already walks a
 * billing matrix does not learn a second variant vocabulary.
 *
 * Closed on purpose: a free-string axis key would let content invent
 * tabs the renderer has no chrome for. `platform` is macOS/Windows;
 * `protocol` is Anthropic/OpenAI — the two tab kinds the live page
 * actually has.
 */
export type DocsVariantAxisKey = 'platform' | 'protocol';

export interface DocsVariantAxisValue {
  readonly key: string;
  readonly label: BilingualLabel;
}

export interface DocsVariantAxis {
  readonly key: DocsVariantAxisKey;
  readonly label: BilingualLabel;
  readonly values: readonly DocsVariantAxisValue[];
}

export type DocsSnippetLanguage = 'bash' | 'powershell' | 'json' | 'python' | 'text';

/**
 * The client surfaces that render this document. Closed, and deliberately
 * coarse: it names a *renderer*, not a device or a session. Anything
 * finer-grained (a plan, an org flag, a role) must not be smuggled in here —
 * that is per-user state, and per-user state belongs in the response body,
 * which this endpoint can now carry because it is session-gated.
 */
export const DOCS_SURFACES = ['console_web', 'desktop'] as const;

export type DocsSurface = (typeof DOCS_SURFACES)[number];

export function isDocsSurface(raw: string): raw is DocsSurface {
  for (const candidate of DOCS_SURFACES) {
    if (candidate === raw) return true;
  }
  return false;
}

export interface DocsCapabilityAvailableCondition {
  readonly when: 'capability_available';
  readonly capability: DocsModelCapability;
}

export interface DocsSurfaceCondition {
  readonly when: 'surface';
  readonly surface: DocsSurface;
}

/**
 * The conditions that consult a fact about the world. Everything else in
 * `DocsVisibleWhen` is boolean structure over these — which is why a renderer
 * supplies a resolver for exactly this type and gets the rest for free.
 *
 * `capability_available` is true when the catalog resolved at least one model
 * for `capability`; `surface` is true when the renderer doing the drawing is
 * the one named.
 */
export type DocsVisibleWhenLeaf = DocsCapabilityAvailableCondition | DocsSurfaceCondition;

/**
 * Optional visibility condition on a section or a block.
 *
 * The seam exists so that *content* can stop being universal without the
 * renderer learning a new expression form — adding content is a router data
 * change and ships with zero frontend releases, while adding an expression
 * form costs one release on every surface. That asymmetry is why this union
 * is generous on day one and closed forever after.
 *
 * Every leaf is evaluated **client-side**, from facts the client already
 * holds before it calls this endpoint — the resolved catalog and its own
 * identity. The server never evaluates these; it ships them verbatim, so one
 * document body stays byte-identical for every caller and the ETag keeps
 * working.
 *
 * A renderer that meets a condition it cannot evaluate must treat the node as
 * **visible**. Hiding on ignorance would silently blank a section for older
 * clients, which is the exact drift this document exists to end.
 *
 * That rule does not survive `not` if it is applied naively — "unknown, so
 * true" negated is "false, so hidden", which is the failure the rule exists
 * to prevent, arrived at through the rule itself. So "cannot evaluate" is a
 * third answer that PROPAGATES rather than a boolean picked early:
 * `evaluateDocsVisibleWhen` below returns `'unknown'`, `not` of `'unknown'`
 * is `'unknown'`, and only `resolveDocsNodeVisibility` at the top collapses
 * it to visible. Renderers should call those two functions rather than write
 * the walk again — the whole point of a closed vocabulary is that its
 * semantics live in one place, and this is the one place.
 */
export type DocsVisibleWhen =
  | DocsVisibleWhenLeaf
  | { readonly when: 'not'; readonly condition: DocsVisibleWhen }
  | { readonly when: 'all'; readonly conditions: readonly DocsVisibleWhen[] }
  | { readonly when: 'any'; readonly conditions: readonly DocsVisibleWhen[] };

/**
 * Carried by every section and every block. Absent means unconditionally
 * visible, which is what the entire first batch ships as — issue #2184 AC 4
 * requires per-section parity with the page this replaces, so a first batch
 * that hid anything would be a regression dressed as a feature.
 */
export interface DocsVisibilityGated {
  readonly visible_when?: DocsVisibleWhen;
}

/**
 * `'unknown'` is not a third truth value in the document's logic — it is the
 * renderer admitting it could not answer, and it is contagious for exactly
 * that reason.
 */
export type DocsVisibilityAnswer = boolean | 'unknown';

/**
 * Answers a leaf from facts the renderer already holds, or `'unknown'` when
 * it holds none — a catalog request that failed, a capability the document
 * never declared. `'unknown'` is the honest answer there, and it is a
 * different thing from `false`.
 */
export type DocsVisibleWhenLeafResolver = (leaf: DocsVisibleWhenLeaf) => DocsVisibilityAnswer;

/**
 * Walks the condition tree, keeping `'unknown'` distinct from `false` at
 * every level.
 *
 * `all` answers `false` as soon as one branch is definitely false, even if a
 * sibling is unknown — a definite refutation is not weakened by an adjacent
 * shrug. `any` answers `true` the same way. Only when neither definite answer
 * is reachable does an unknown branch make the whole node unknown.
 *
 * The two empty cases fall out of that and are worth naming: `all` over no
 * conditions is vacuously `true`, while `any` over no conditions answers
 * `'unknown'` rather than the textbook `false`. An empty list reaches a
 * renderer only through an authoring slip or a dropped field, and "I cannot
 * tell" describes that better than "the answer is no" — which, at the top,
 * is the difference between showing a section and silently deleting it.
 */
export function evaluateDocsVisibleWhen(
  condition: DocsVisibleWhen,
  resolveLeaf: DocsVisibleWhenLeafResolver,
): DocsVisibilityAnswer {
  switch (condition.when) {
    case 'capability_available':
    case 'surface':
      return resolveLeaf(condition);
    case 'not': {
      const inner = evaluateDocsVisibleWhen(condition.condition, resolveLeaf);
      return inner === 'unknown' ? 'unknown' : !inner;
    }
    case 'all': {
      let unknown = false;
      for (const nested of condition.conditions) {
        const answer = evaluateDocsVisibleWhen(nested, resolveLeaf);
        if (answer === false) return false;
        if (answer === 'unknown') unknown = true;
      }
      return unknown ? 'unknown' : true;
    }
    case 'any': {
      if (condition.conditions.length === 0) return 'unknown';
      let unknown = false;
      for (const nested of condition.conditions) {
        const answer = evaluateDocsVisibleWhen(nested, resolveLeaf);
        if (answer === true) return true;
        if (answer === 'unknown') unknown = true;
      }
      return unknown ? 'unknown' : false;
    }
    default:
      // Unreachable by the type, reachable over the wire: a `when` router
      // shipped after this client was built. The client is older than the
      // document it was handed, which is the normal case, not the edge one.
      return 'unknown';
  }
}

/**
 * The one call a renderer makes per section and per block. Absent
 * `visible_when` is visible, and so is anything the resolver could not
 * decide — this is the single place the "unknown means show it" rule is
 * applied, and the only place it may be.
 */
export function resolveDocsNodeVisibility(
  node: DocsVisibilityGated,
  resolveLeaf: DocsVisibleWhenLeafResolver,
): boolean {
  if (node.visible_when === undefined) return true;
  const answer = evaluateDocsVisibleWhen(node.visible_when, resolveLeaf);
  return answer === 'unknown' ? true : answer;
}

export interface DocsCalloutBlock extends DocsVisibilityGated {
  readonly type: 'callout';
  readonly tone: 'note' | 'warning' | 'fact';
  readonly title: BilingualLabel | null;
  readonly body: readonly BilingualLabel[];
}

export interface DocsSnippetVariant {
  /** Axis key → value key, e.g. `{ platform: 'windows' }`. Empty for the single-variant case. */
  readonly select: Readonly<Record<string, string>>;
  readonly language: DocsSnippetLanguage;
  readonly template: string;
}

export interface DocsSnippetBlock extends DocsVisibilityGated {
  readonly type: 'snippet';
  readonly title: BilingualLabel | null;
  readonly caption: BilingualLabel | null;
  readonly variants: readonly DocsSnippetVariant[];
}

export type DocsTableCell =
  | { readonly kind: 'text'; readonly value: BilingualLabel }
  | { readonly kind: 'code'; readonly value: string }
  | { readonly kind: 'empty' };

export interface DocsParamTableRow {
  /** Column key → cell. A cell is bilingual prose or a code literal. */
  readonly cells: Readonly<Record<string, DocsTableCell>>;
}

export interface DocsParamTableBlock extends DocsVisibilityGated {
  readonly type: 'param_table';
  readonly caption: BilingualLabel | null;
  readonly columns: readonly { readonly key: string; readonly label: BilingualLabel }[];
  readonly rows: readonly DocsParamTableRow[];
}

export interface DocsStepsBlock extends DocsVisibilityGated {
  readonly type: 'steps';
  readonly steps: readonly {
    readonly title: BilingualLabel;
    readonly body: readonly BilingualLabel[];
  }[];
}

export interface DocsChecklistBlock extends DocsVisibilityGated {
  readonly type: 'checklist';
  readonly title: BilingualLabel | null;
  readonly items: readonly BilingualLabel[];
}

export interface DocsKeyValuesBlock extends DocsVisibilityGated {
  readonly type: 'key_values';
  readonly title: BilingualLabel | null;
  readonly entries: readonly {
    readonly label: BilingualLabel;
    readonly value: DocsTableCell;
  }[];
}

export interface DocsLinkBlock extends DocsVisibilityGated {
  readonly type: 'link';
  readonly label: BilingualLabel;
  /** Absolute URL, or a placeholder-bearing string such as `{{console_origin}}/keys`. */
  readonly href: string;
  readonly external: boolean;
}

export type DocsBlock =
  | DocsCalloutBlock
  | DocsSnippetBlock
  | DocsParamTableBlock
  | DocsStepsBlock
  | DocsChecklistBlock
  | DocsKeyValuesBlock
  | DocsLinkBlock;

export interface DocsAccessSection extends DocsVisibilityGated {
  readonly id: string;
  readonly title: BilingualLabel;
  /**
   * Short TOC label. Null (or absent on a previously published row) means
   * the renderer falls back to `title`. Additive optional member — does
   * not bump `presentation_version`.
   */
  readonly nav_label: BilingualLabel | null;
  readonly summary: BilingualLabel | null;
  readonly variant_axes: readonly DocsVariantAxis[];
  readonly blocks: readonly DocsBlock[];
}

export interface DocsAccessDocument {
  readonly presentation_version: number;
  /** Content date, a literal in the source — NOT build time, so the body is byte-stable. */
  readonly updated_at: string;
  readonly brand: BrandId;
  readonly locales: readonly ['zh-CN', 'en'];
  readonly model_selectors: readonly DocsModelSelector[];
  readonly sections: readonly DocsAccessSection[];
}
